// src/screens/TehuacanScreen.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  ArrowUpRight,
  Building2,
  Calculator,
  CheckCircle2,
  FileCheck2,
  MapPin,
  ReceiptText,
  ShieldCheck,
  Users,
} from "lucide-react";

const PAGE_URL = "https://solcont.com.mx/contador-tehuacan";
const OG_IMAGE = "https://solcont.com.mx/og-image.jpg";
const LANGUAGE_STORAGE_KEY = "solcont-language";
const LANGUAGE_CHANGE_EVENT = "solcont:language-change";
const LOCALES = { es: "es-MX", en: "en-US", fr: "fr-CA" };
const SERVICE_ICONS = [Calculator, ReceiptText, FileCheck2, ShieldCheck, Users, Building2];

// Mismo contrato que App: prop language, evento compartido y localStorage.
function normalizeLanguage(value) {
  if (typeof value !== "string") return null;
  const language = value.trim().toLowerCase().split(/[-_]/)[0];
  return Object.prototype.hasOwnProperty.call(LOCALES, language) ? language : null;
}

function readStoredLanguage() {
  if (typeof window === "undefined") return "es";
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY)) || "es";
  } catch {
    return "es";
  }
}

function usePageLanguage(controlledLanguage) {
  const [storedLanguage, setStoredLanguage] = useState(readStoredLanguage);

  useEffect(() => {
    const handleLanguageChange = (event) => {
      const value = typeof event.detail === "string" ? event.detail : event.detail?.language;
      const nextLanguage = normalizeLanguage(value);
      if (!nextLanguage) return;
      setStoredLanguage(nextLanguage);
      try {
        window.localStorage.setItem(LANGUAGE_STORAGE_KEY, nextLanguage);
      } catch {
        // El selector funciona también con el almacenamiento bloqueado.
      }
    };
    const handleStorageChange = (event) => {
      if (event.key === LANGUAGE_STORAGE_KEY || event.key === null) {
        setStoredLanguage(readStoredLanguage());
      }
    };
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  return normalizeLanguage(controlledLanguage) || storedLanguage;
}

const COPY = {
  es: {
    title: "Contador en Tehuacán, Puebla | SolCont México",
    description: "Contador en Tehuacán para personas físicas y empresas. Contabilidad, declaraciones SAT, RESICO, regularización fiscal, nómina e IMSS con SolCont México.",
    imageAlt: "SolCont México — Contabilidad y estrategia fiscal",
    home: "Inicio",
    breadcrumb: "Ruta de navegación",
    eyebrow: "SolCont / Tehuacán, Puebla",
    heroLead: "Contador en",
    heroAudience: "Para personas y empresas.",
    heroText: "Contabilidad, declaraciones ante el SAT, RESICO, regularización fiscal, nómina e IMSS. Atención clara y seguimiento profesional.",
    book: "Agenda una asesoría",
    viewServices: "Ver servicios",
    languages: "Atención en español, inglés y francés.",
    needsTitle: "¿Qué necesitas resolver?",
    needs: [
      "Llevar correctamente mi contabilidad",
      "Cambiar de contador",
      "Regularizarme ante el SAT",
      "Entender mi régimen fiscal o RESICO",
      "Recibir apoyo con nómina o mi empresa",
    ],
    needsNote: "Primero revisamos tu situación. Después definimos los siguientes pasos.",
    introLabel: "Cercanía local. Respaldo digital.",
    introTitle: "Tu contabilidad, con claridad y dirección.",
    introParagraphs: [
      "En SolCont México atendemos a contribuyentes, profesionistas, negocios y empresas de Tehuacán que buscan llevar sus obligaciones fiscales con orden, información útil y seguimiento.",
      "Te acompañamos en el cumplimiento mensual y en situaciones que requieren una revisión más profunda: cambiar de contador, atender declaraciones pendientes, regularizarte ante el SAT o revisar las necesidades de tu empresa.",
    ],
    servicesLabel: "Servicios contables y fiscales",
    servicesTitle: "Un servicio para tu situación.",
    servicesText: "El alcance se define según tu actividad, régimen y obligaciones. No todos los negocios necesitan lo mismo.",
    catalogTitle: "Servicios contables y fiscales en Tehuacán",
    services: [
      { title: "Contabilidad mensual", text: "Registro y revisión contable para personas físicas, profesionistas, negocios y empresas de Tehuacán." },
      { title: "Declaraciones ante el SAT", text: "Cálculo y presentación de declaraciones periódicas, revisión de obligaciones y seguimiento del cumplimiento." },
      { title: "Regularización fiscal", text: "Revisión de declaraciones pendientes, opinión de cumplimiento, requerimientos y situación fiscal ante el SAT." },
      { title: "RESICO y personas físicas", text: "Acompañamiento para actividades empresariales, profesionales y RESICO, sujeto a la revisión del régimen aplicable." },
      { title: "Nómina e IMSS", text: "Apoyo con nómina y obligaciones patronales ante el IMSS e INFONAVIT para negocios que cuentan con personal." },
      { title: "Personas morales", text: "Contabilidad, declaraciones y estrategia fiscal para sociedades con operación en Tehuacán y la región." },
    ],
    audienceLabel: "Personas, negocios y empresas",
    audienceTitle: "Si tu actividad crece, tu contabilidad debe acompañarte.",
    audienceText: "Atendemos distintas actividades en Tehuacán y la región, con procesos adaptados a cada caso.",
    clients: ["Comercios y tiendas", "Profesionistas independientes", "Freelancers y prestadores de servicios", "Restaurantes y negocios de alimentos", "Empresas y personas morales", "Contribuyentes que necesitan regularizarse"],
    processLabel: "Cómo trabajamos",
    processTitle: "Primero entendemos tu situación.",
    steps: [
      { title: "Revisamos tu caso", text: "Nos explicas qué necesitas: contabilidad, cambio de contador, regularización, RESICO, nómina o empresa." },
      { title: "Definimos el alcance", text: "Identificamos la información necesaria, delimitamos el servicio y acordamos los siguientes pasos." },
      { title: "Damos seguimiento", text: "Trabajamos con un proceso claro para que conozcas qué se está atendiendo y qué sigue." },
    ],
    faqLabel: "Preguntas frecuentes",
    faqTitle: "Contador en Tehuacán: tus dudas, claras.",
    faqs: [
      { q: "¿SolCont ofrece servicio de contador en Tehuacán?", a: "Sí. Atendemos contribuyentes y negocios de Tehuacán, Puebla, además de ofrecer servicio contable digital para clientes en otras partes de México." },
      { q: "¿Pueden ayudarme si quiero cambiar de contador?", a: "Sí. Podemos revisar tu situación fiscal, obligaciones y declaraciones presentadas o pendientes para definir una transición ordenada." },
      { q: "¿Atienden personas en RESICO?", a: "Sí. Atendemos contribuyentes en RESICO, sujeto a la revisión de su situación y obligaciones fiscales." },
      { q: "¿Pueden revisar si tengo declaraciones pendientes ante el SAT?", a: "Sí. La revisión puede incluir constancia de situación fiscal, obligaciones, opinión de cumplimiento y documentación disponible para determinar los siguientes pasos." },
    ],
    ctaLabel: "SolCont México / Tehuacán",
    ctaTitle: "El primer paso es tener claridad.",
    ctaText: "Agenda una asesoría para revisar tu situación fiscal y definir cómo podemos ayudarte.",
  },
  en: {
    title: "Accountant in Tehuacán, Puebla | SolCont México",
    description: "Accountant in Tehuacán for individuals and businesses. Bookkeeping, SAT tax returns, RESICO, tax regularization, payroll and IMSS with SolCont México.",
    imageAlt: "SolCont México — Accounting and tax strategy",
    home: "Home",
    breadcrumb: "Breadcrumb navigation",
    eyebrow: "SolCont / Tehuacán, Puebla",
    heroLead: "Accountant in",
    heroAudience: "For individuals and businesses.",
    heroText: "Accounting, SAT tax returns, RESICO, tax regularization, payroll and IMSS. Clear communication and professional follow-up.",
    book: "Book a consultation",
    viewServices: "View services",
    languages: "Support in Spanish, English and French.",
    needsTitle: "What do you need help with?",
    needs: ["Keeping my accounting in order", "Changing accountants", "Resolving outstanding SAT obligations", "Understanding my tax regime or RESICO", "Getting payroll or business support"],
    needsNote: "First, we review your situation. Then, we define the next steps.",
    introLabel: "Local knowledge. Digital support.",
    introTitle: "Accounting with clarity and direction.",
    introParagraphs: [
      "At SolCont México, we work with individuals, independent professionals and businesses in Tehuacán who want to manage their tax obligations with organized records, useful information and ongoing support.",
      "We help with monthly compliance and situations that need a closer look: changing accountants, addressing overdue returns, resolving SAT obligations or reviewing your company's accounting needs.",
    ],
    servicesLabel: "Accounting and tax services",
    servicesTitle: "Support that fits your situation.",
    servicesText: "The scope depends on your activity, tax regime and obligations. Different businesses have different needs.",
    catalogTitle: "Accounting and tax services in Tehuacán",
    services: [
      { title: "Monthly accounting", text: "Bookkeeping and accounting review for individuals, independent professionals and businesses in Tehuacán." },
      { title: "SAT tax returns", text: "Tax calculations and periodic filings, review of obligations and compliance follow-up." },
      { title: "Tax regularization", text: "Review of overdue returns, tax compliance status, official notices and your situation with the SAT." },
      { title: "RESICO and individuals", text: "Support for business and professional activities and RESICO taxpayers, subject to a review of the applicable tax regime." },
      { title: "Payroll and IMSS", text: "Support with payroll and employer obligations before IMSS and INFONAVIT for businesses with employees." },
      { title: "Companies", text: "Accounting, tax returns and tax strategy for companies operating in Tehuacán and the surrounding region." },
    ],
    audienceLabel: "Individuals and businesses",
    audienceTitle: "As your business grows, your accounting should keep up.",
    audienceText: "We support different business activities in Tehuacán and the surrounding region, with processes tailored to each case.",
    clients: ["Shops and retailers", "Independent professionals", "Freelancers and service providers", "Restaurants and food businesses", "Companies and legal entities", "Taxpayers with outstanding obligations"],
    processLabel: "How we work",
    processTitle: "We start by understanding your situation.",
    steps: [
      { title: "Review your case", text: "Tell us what you need: accounting, a change of accountant, tax regularization, RESICO, payroll or company support." },
      { title: "Define the scope", text: "We identify the information needed, define the service and agree on the next steps." },
      { title: "Follow through", text: "We use a clear process so you know what is being addressed and what comes next." },
    ],
    faqLabel: "Frequently asked questions",
    faqTitle: "Accounting in Tehuacán: clear answers.",
    faqs: [
      { q: "Does SolCont offer accounting services in Tehuacán?", a: "Yes. We serve individuals and businesses in Tehuacán, Puebla, and provide digital accounting services for clients elsewhere in Mexico." },
      { q: "Can you help me change accountants?", a: "Yes. We can review your tax situation, obligations, filed returns and outstanding returns to plan an orderly transition." },
      { q: "Do you work with RESICO taxpayers?", a: "Yes. We support RESICO taxpayers, subject to a review of their situation and tax obligations." },
      { q: "Can you check whether I have overdue SAT returns?", a: "Yes. The review may include your tax registration certificate, obligations, compliance status and available records to determine the next steps." },
    ],
    ctaLabel: "SolCont México / Tehuacán",
    ctaTitle: "Clarity is the first step.",
    ctaText: "Book a consultation to review your tax situation and discuss how we can help.",
  },
  fr: {
    title: "Comptable à Tehuacán, Puebla | SolCont México",
    description: "Comptable à Tehuacán pour particuliers et entreprises. Comptabilité, déclarations au SAT, RESICO, régularisation fiscale, paie et IMSS avec SolCont México.",
    imageAlt: "SolCont México — Comptabilité et stratégie fiscale",
    home: "Accueil",
    breadcrumb: "Fil d’Ariane",
    eyebrow: "SolCont / Tehuacán, Puebla",
    heroLead: "Comptable à",
    heroAudience: "Pour particuliers et entreprises.",
    heroText: "Comptabilité, déclarations au SAT, RESICO, régularisation fiscale, paie et IMSS. Une communication claire et un suivi professionnel.",
    book: "Prendre rendez-vous",
    viewServices: "Voir les services",
    languages: "Accompagnement en espagnol, anglais et français.",
    needsTitle: "De quoi avez-vous besoin ?",
    needs: ["Tenir ma comptabilité à jour", "Changer de comptable", "Régulariser ma situation auprès du SAT", "Comprendre mon régime fiscal ou le RESICO", "Obtenir du soutien pour la paie ou mon entreprise"],
    needsNote: "Nous examinons d’abord votre situation, puis définissons les prochaines étapes.",
    introLabel: "Proximité locale. Services numériques.",
    introTitle: "Une comptabilité claire pour mieux décider.",
    introParagraphs: [
      "Chez SolCont México, nous accompagnons les particuliers, professionnels indépendants et entreprises de Tehuacán qui souhaitent gérer leurs obligations fiscales avec des dossiers organisés, des informations utiles et un suivi régulier.",
      "Nous vous aidons avec vos obligations mensuelles et les situations qui nécessitent un examen approfondi : changement de comptable, déclarations en retard, régularisation auprès du SAT ou besoins comptables de votre entreprise.",
    ],
    servicesLabel: "Services comptables et fiscaux",
    servicesTitle: "Un accompagnement adapté à votre situation.",
    servicesText: "L’étendue du service dépend de votre activité, de votre régime fiscal et de vos obligations. Chaque entreprise a ses propres besoins.",
    catalogTitle: "Services comptables et fiscaux à Tehuacán",
    services: [
      { title: "Comptabilité mensuelle", text: "Tenue et révision comptables pour particuliers, professionnels indépendants et entreprises de Tehuacán." },
      { title: "Déclarations au SAT", text: "Calcul des impôts, dépôt des déclarations périodiques, examen des obligations et suivi de la conformité." },
      { title: "Régularisation fiscale", text: "Examen des déclarations en retard, de la situation de conformité et des demandes du SAT." },
      { title: "RESICO et particuliers", text: "Accompagnement des activités commerciales et professionnelles et des contribuables au RESICO, après examen du régime applicable." },
      { title: "Paie et IMSS", text: "Soutien pour la paie et les obligations patronales auprès de l’IMSS et de l’INFONAVIT pour les entreprises ayant du personnel." },
      { title: "Sociétés", text: "Comptabilité, déclarations et stratégie fiscale pour les sociétés présentes à Tehuacán et dans la région." },
    ],
    audienceLabel: "Particuliers et entreprises",
    audienceTitle: "Votre activité évolue. Votre comptabilité aussi.",
    audienceText: "Nous accompagnons différents secteurs à Tehuacán et dans la région, avec des processus adaptés à chaque situation.",
    clients: ["Commerces et boutiques", "Professionnels indépendants", "Travailleurs autonomes et prestataires de services", "Restaurants et commerces alimentaires", "Entreprises et personnes morales", "Contribuables ayant des obligations en retard"],
    processLabel: "Notre méthode",
    processTitle: "Comprendre votre situation avant tout.",
    steps: [
      { title: "Étudier votre dossier", text: "Présentez-nous vos besoins : comptabilité, changement de comptable, régularisation, RESICO, paie ou société." },
      { title: "Définir le mandat", text: "Nous identifions les informations nécessaires, précisons le service et convenons des prochaines étapes." },
      { title: "Assurer le suivi", text: "Un processus clair vous permet de savoir ce qui est en cours et ce qui vient ensuite." },
    ],
    faqLabel: "Questions fréquentes",
    faqTitle: "Comptabilité à Tehuacán : des réponses claires.",
    faqs: [
      { q: "SolCont propose-t-il des services comptables à Tehuacán ?", a: "Oui. Nous accompagnons les particuliers et les entreprises de Tehuacán, Puebla, et proposons des services comptables numériques ailleurs au Mexique." },
      { q: "Pouvez-vous m’aider à changer de comptable ?", a: "Oui. Nous pouvons examiner votre situation fiscale, vos obligations et vos déclarations déposées ou en retard afin de préparer une transition ordonnée." },
      { q: "Accompagnez-vous les contribuables au RESICO ?", a: "Oui. Nous accompagnons les contribuables au RESICO après examen de leur situation et de leurs obligations fiscales." },
      { q: "Pouvez-vous vérifier si j’ai des déclarations en retard au SAT ?", a: "Oui. L’examen peut inclure votre attestation de situation fiscale, vos obligations, votre état de conformité et les documents disponibles afin de déterminer les prochaines étapes." },
    ],
    ctaLabel: "SolCont México / Tehuacán",
    ctaTitle: "La première étape : y voir clair.",
    ctaText: "Prenez rendez-vous pour examiner votre situation fiscale et voir comment nous pouvons vous accompagner.",
  },
};

// CSS aislado: App ya compensa las 4rem del navbar. Sin alturas fijas,
// recortes de texto, autofocus ni estilos globales que afecten otras rutas.
const PAGE_STYLES = `
  .solcont-tehuacan {
    --st-blue: #1648EE; --st-navy: #081B3E; --st-ivory: #F7F3E9; --st-pink: #F72585;
    --st-space: clamp(2.5rem, 6vh, 4.5rem); --st-hero-space: clamp(1.75rem, 5vh, 3.5rem);
    --st-gap: clamp(1.5rem, 3vw, 3rem); --st-panel: clamp(1.25rem, 2.2vw, 2rem);
    color: var(--st-navy); background: var(--st-ivory);
    font-family: "DM Sans", Arial, sans-serif; font-size: 1rem; line-height: 1.6;
  }
  .solcont-tehuacan *, .solcont-tehuacan *::before, .solcont-tehuacan *::after { box-sizing: border-box; }
  .solcont-tehuacan :is(h1, h2, h3, p, ul, ol) { margin: 0; }
  .solcont-tehuacan :is(ul, ol) { padding: 0; list-style: none; }
  .solcont-tehuacan a { text-decoration: none; }
  .solcont-tehuacan :is(a, summary):focus-visible { outline: 3px solid currentColor; outline-offset: 5px; }
  .solcont-tehuacan section[id] { scroll-margin-top: 5rem; }
  .solcont-tehuacan .st-container { width: 100%; max-width: 75rem; margin-inline: auto; padding-inline: clamp(1rem, 3vw, 2.5rem); }
  .solcont-tehuacan .st-section { padding-block: var(--st-space); }
  .solcont-tehuacan .st-split { display: grid; grid-template-columns: minmax(0, 1fr); gap: var(--st-gap); }
  .solcont-tehuacan .st-split > *, .solcont-tehuacan .st-grid > * { min-width: 0; }
  .solcont-tehuacan .st-display { font-family: "Arial Narrow", "Roboto Condensed", Arial, sans-serif; font-stretch: condensed; font-weight: 800; letter-spacing: -.025em; text-wrap: balance; overflow-wrap: anywhere; }
  .solcont-tehuacan .st-title { margin-top: .75rem; font-size: clamp(1.875rem, 3.5vw, 2.75rem); line-height: 1.1; }
  .solcont-tehuacan .st-label { font-size: .75rem; font-weight: 700; line-height: 1.6; letter-spacing: .14em; text-transform: uppercase; }
  .solcont-tehuacan .st-accent { display: block; width: 3.25rem; height: 4px; margin-block: 1rem; background: var(--st-pink); border-radius: 2px; }
  .solcont-tehuacan .st-copy { color: #41516B; max-width: 44rem; }
  .solcont-tehuacan .st-copy p + p { margin-top: 1rem; }
  .solcont-tehuacan .st-heading { max-width: 45rem; }
  .solcont-tehuacan .st-heading > p:last-child { margin-top: 1rem; }
  .solcont-tehuacan .st-blue { color: var(--st-blue); }
  .solcont-tehuacan .st-hero { position: relative; isolation: isolate; color: var(--st-ivory); background: var(--st-blue); }
  .solcont-tehuacan .st-pattern { position: absolute; inset: 0; z-index: -1; overflow: hidden; pointer-events: none; background-image: linear-gradient(#ffffff10 1px, transparent 1px), linear-gradient(90deg, #ffffff10 1px, transparent 1px); background-size: 64px 64px; }
  .solcont-tehuacan .st-orbit { position: absolute; top: -9rem; right: -11rem; width: 34rem; height: 34rem; border: 1px solid #ffffff40; border-radius: 50%; }
  .solcont-tehuacan .st-orbit::before { content: ""; position: absolute; inset: 4.5rem; border: 1px solid #ffffff40; border-radius: 50%; }
  .solcont-tehuacan .st-orbit::after { content: ""; position: absolute; left: 3.7rem; bottom: 5rem; width: 1rem; height: 1rem; border-radius: 50%; background: var(--st-pink); }
  .solcont-tehuacan .st-hero-inner { padding-block: var(--st-hero-space); align-items: center; }
  .solcont-tehuacan .st-location { display: flex; align-items: center; gap: .5rem; }
  .solcont-tehuacan .st-location svg { flex-shrink: 0; width: 1rem; height: 1rem; }
  .solcont-tehuacan .st-hero-title { font-size: clamp(2.25rem, 7vw, 3.5rem); line-height: 1.02; text-transform: uppercase; }
  .solcont-tehuacan .st-hero-title span { display: block; }
  .solcont-tehuacan .st-hero-subtitle { margin-top: 1rem; font-size: clamp(1.125rem, 2vw, 1.375rem); font-weight: 700; line-height: 1.35; }
  .solcont-tehuacan .st-hero-copy { margin-top: .75rem; max-width: 37rem; color: #FFFFFFE6; }
  .solcont-tehuacan .st-actions { display: flex; flex-wrap: wrap; gap: .75rem; margin-top: 1.5rem; }
  .solcont-tehuacan .st-button { display: inline-flex; align-items: center; justify-content: center; gap: .6rem; min-height: 2.75rem; max-width: 100%; padding: .75rem 1.15rem; border: 1px solid transparent; border-radius: 999px; font-size: .9375rem; font-weight: 700; line-height: 1.4; text-align: center; overflow-wrap: anywhere; }
  .solcont-tehuacan .st-button svg { width: 1.125rem; height: 1.125rem; flex-shrink: 0; }
  .solcont-tehuacan .st-primary { background: var(--st-pink); color: var(--st-navy); }
  .solcont-tehuacan .st-secondary { border-color: #ffffff70; color: var(--st-ivory); }
  .solcont-tehuacan .st-button:focus-visible { outline-color: var(--st-ivory); }
  .solcont-tehuacan .st-language-note { margin-top: 1rem; font-size: .8125rem; color: #FFFFFFD9; }
  .solcont-tehuacan .st-panel { padding: var(--st-panel); border-radius: 1.25rem; background: var(--st-ivory); color: var(--st-navy); }
  .solcont-tehuacan .st-rule { display: flex; align-items: center; gap: .75rem; margin-bottom: 1.25rem; }
  .solcont-tehuacan .st-rule::before { content: ""; width: .625rem; height: .625rem; border-radius: 50%; background: var(--st-pink); }
  .solcont-tehuacan .st-rule::after { content: ""; flex: 1; height: 1px; background: currentColor; }
  .solcont-tehuacan .st-panel-title { font-size: clamp(1.25rem, 2vw, 1.625rem); line-height: 1.2; }
  .solcont-tehuacan .st-needs { margin-top: 1rem; }
  .solcont-tehuacan .st-needs li { display: grid; grid-template-columns: 1.75rem minmax(0, 1fr); align-items: start; gap: .75rem; padding-block: .625rem; border-bottom: 1px solid #081B3E24; }
  .solcont-tehuacan .st-needs li:last-child { border-bottom: 0; }
  .solcont-tehuacan .st-number { display: inline-flex; align-items: center; justify-content: center; width: 1.75rem; min-height: 1.75rem; color: var(--st-blue); font-size: .75rem; font-weight: 700; font-variant-numeric: tabular-nums; }
  .solcont-tehuacan .st-needs-text { font-size: .9375rem; font-weight: 600; line-height: 1.6; overflow-wrap: anywhere; }
  .solcont-tehuacan .st-panel-note { margin-top: 1rem; padding-top: 1rem; border-top: 1px solid #081B3E24; color: #41516B; font-size: .8125rem; }
  .solcont-tehuacan .st-breadcrumb { padding-block: 1rem; font-size: .8125rem; border-bottom: 1px solid #081B3E24; }
  .solcont-tehuacan .st-breadcrumb ol { display: flex; flex-wrap: wrap; align-items: center; gap: .75rem; }
  .solcont-tehuacan .st-breadcrumb a { display: inline-flex; align-items: center; min-height: 2.75rem; color: var(--st-blue); text-decoration: underline; text-underline-offset: 3px; }
  .solcont-tehuacan .st-services { border-block: 1px solid #081B3E24; background: #FFFDFA; }
  .solcont-tehuacan .st-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 1.25rem; margin-top: 2rem; }
  .solcont-tehuacan .st-service { border: 1px solid #081B3E24; border-radius: 1rem; background: var(--st-ivory); padding: var(--st-panel); }
  .solcont-tehuacan .st-service-top { display: flex; justify-content: space-between; align-items: center; padding-bottom: 1rem; border-bottom: 1px solid #081B3E; color: var(--st-blue); }
  .solcont-tehuacan .st-service-top svg { width: 1.5rem; height: 1.5rem; }
  .solcont-tehuacan .st-service-top span { font-size: .75rem; font-weight: 700; font-variant-numeric: tabular-nums; }
  .solcont-tehuacan .st-card-title { margin-top: 1rem; font-size: 1.375rem; line-height: 1.2; }
  .solcont-tehuacan .st-service p { margin-top: .75rem; color: #41516B; }
  .solcont-tehuacan .st-audience { padding: var(--st-panel); border-radius: 1.25rem; color: var(--st-ivory); background: var(--st-navy); }
  .solcont-tehuacan .st-audience .st-label { color: #FF91C3; }
  .solcont-tehuacan .st-audience-copy { margin-top: 1rem; color: #FFFFFFD9; }
  .solcont-tehuacan .st-clients { display: grid; grid-template-columns: minmax(0, 1fr); align-content: start; }
  .solcont-tehuacan .st-clients li { display: flex; align-items: start; gap: .75rem; padding-block: .875rem; border-bottom: 1px solid #ffffff30; }
  .solcont-tehuacan .st-clients svg { width: 1.125rem; height: 1.125rem; margin-top: .25rem; color: #FF91C3; flex-shrink: 0; }
  .solcont-tehuacan .st-clients span { overflow-wrap: anywhere; }
  .solcont-tehuacan .st-process { padding-top: 0; }
  .solcont-tehuacan .st-step { padding-top: 1.25rem; border-top: 1px solid var(--st-navy); }
  .solcont-tehuacan .st-step-number { color: var(--st-blue); font-size: 2.5rem; line-height: 1; }
  .solcont-tehuacan .st-step p { margin-top: .75rem; color: #41516B; }
  .solcont-tehuacan .st-faq-section { background: #FFFDFA; border-top: 1px solid #081B3E24; }
  .solcont-tehuacan .st-faq { border-top: 1px solid #081B3E; }
  .solcont-tehuacan .st-faq details { border-bottom: 1px solid #081B3E24; }
  .solcont-tehuacan .st-faq summary { display: flex; align-items: center; justify-content: space-between; gap: 1rem; min-height: 3.5rem; padding-block: 1rem; cursor: pointer; list-style: none; font-weight: 700; overflow-wrap: anywhere; }
  .solcont-tehuacan .st-faq summary::-webkit-details-marker { display: none; }
  .solcont-tehuacan .st-faq-plus { display: inline-block; color: var(--st-blue); font-size: 1.5rem; line-height: 1; flex-shrink: 0; }
  .solcont-tehuacan .st-faq details[open] .st-faq-plus { transform: rotate(45deg); }
  .solcont-tehuacan .st-faq details p { padding-bottom: 1.25rem; color: #41516B; }
  .solcont-tehuacan .st-cta { color: var(--st-ivory); background: var(--st-blue); }
  .solcont-tehuacan .st-cta-inner { display: flex; flex-direction: column; align-items: start; gap: 1.5rem; }
  .solcont-tehuacan .st-cta-copy { max-width: 42rem; min-width: 0; }
  .solcont-tehuacan .st-cta-copy > p:last-child { margin-top: 1rem; color: #FFFFFFE6; }
  .solcont-tehuacan .st-cta .st-button { flex-shrink: 0; }
  @supports (height: 100svh) {
    .solcont-tehuacan { --st-space: clamp(2.5rem, 6svh, 4.5rem); --st-hero-space: clamp(1.75rem, 5svh, 3.5rem); }
  }
  @media (min-width: 40rem) {
    .solcont-tehuacan .st-service-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  }
  @media (min-width: 60rem) {
    .solcont-tehuacan .st-split { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); }
    .solcont-tehuacan .st-hero-inner { grid-template-columns: minmax(0, 1.2fr) minmax(0, 1fr); }
    .solcont-tehuacan .st-intro, .solcont-tehuacan .st-faq-layout { grid-template-columns: minmax(0, .85fr) minmax(0, 1.15fr); }
    .solcont-tehuacan .st-hero-title { font-size: clamp(2.5rem, min(4.3vw, 7.5vh), 3.75rem); }
    .solcont-tehuacan .st-service-grid, .solcont-tehuacan .st-step-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); }
    .solcont-tehuacan .st-cta-inner { flex-direction: row; align-items: center; justify-content: space-between; }
  }
  @media (min-width: 60rem) and (max-height: 46.25rem) {
    .solcont-tehuacan { --st-space: 2.25rem; --st-hero-space: 1.5rem; --st-panel: 1.25rem; }
    .solcont-tehuacan .st-hero-title { font-size: clamp(2.5rem, 4vw, 3.25rem); }
    .solcont-tehuacan .st-title { font-size: 2rem; }
    .solcont-tehuacan .st-actions { margin-top: 1rem; }
  }
  @media (max-width: 29.99rem) {
    .solcont-tehuacan .st-actions { flex-direction: column; }
    .solcont-tehuacan .st-actions .st-button, .solcont-tehuacan .st-cta .st-button { width: 100%; }
    .solcont-tehuacan .st-orbit { opacity: .6; }
  }
  @media (hover: hover) {
    .solcont-tehuacan .st-primary:hover { background: #FF74BC; }
    .solcont-tehuacan .st-secondary:hover { background: #ffffff15; }
    .solcont-tehuacan .st-faq summary:hover { color: var(--st-blue); }
  }
`;

function buildSchema(t, language) {
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${PAGE_URL}#webpage`,
        url: PAGE_URL,
        name: t.title,
        description: t.description,
        inLanguage: LOCALES[language],
        isPartOf: { "@id": "https://solcont.com.mx/#website" },
        about: { "@id": `${PAGE_URL}#accounting-service` },
        breadcrumb: { "@id": `${PAGE_URL}#breadcrumb` },
      },
      {
        "@type": "AccountingService",
        "@id": `${PAGE_URL}#accounting-service`,
        name: "SolCont México",
        url: PAGE_URL,
        image: OG_IMAGE,
        description: t.description,
        parentOrganization: { "@id": "https://solcont.com.mx/#org" },
        areaServed: [
          { "@type": "City", name: "Tehuacán", containedInPlace: { "@type": "AdministrativeArea", name: "Puebla" } },
          { "@type": "Country", name: "México" },
        ],
        knowsLanguage: ["es", "en", "fr"],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: t.catalogTitle,
          itemListElement: t.services.map((service) => ({
            "@type": "Offer",
            itemOffered: {
              "@type": "Service",
              name: service.title,
              description: service.text,
              areaServed: { "@type": "City", name: "Tehuacán" },
            },
          })),
        },
      },
      {
        "@type": "BreadcrumbList",
        "@id": `${PAGE_URL}#breadcrumb`,
        itemListElement: [
          { "@type": "ListItem", position: 1, name: t.home, item: "https://solcont.com.mx/" },
          { "@type": "ListItem", position: 2, name: "Tehuacán", item: PAGE_URL },
        ],
      },
    ],
  };
}

export default function TehuacanScreen({ language: controlledLanguage } = {}) {
  const language = usePageLanguage(controlledLanguage);
  const t = COPY[language];
  const schema = buildSchema(t, language);

  return (
    <div className="solcont-tehuacan" lang={LOCALES[language]}>
      <style>{PAGE_STYLES}</style>
      <Helmet>
        {/* App controla el idioma de <html>. No se fuerza español al navegar. */}
        <title>{t.title}</title>
        <meta name="description" content={t.description} />
        <meta name="robots" content="index,follow,max-snippet:-1,max-image-preview:large,max-video-preview:-1" />
        <link rel="canonical" href={PAGE_URL} />
        {/* Una URL real. Añadir hreflang solo cuando existan rutas por idioma. */}
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={LOCALES[language].replace("-", "_")} />
        <meta property="og:site_name" content="SolCont México" />
        <meta property="og:title" content={t.title} />
        <meta property="og:description" content={t.description} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:image" content={OG_IMAGE} />
        <meta property="og:image:alt" content={t.imageAlt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t.title} />
        <meta name="twitter:description" content={t.description} />
        <meta name="twitter:image" content={OG_IMAGE} />
        <meta name="twitter:image:alt" content={t.imageAlt} />
        <script type="application/ld+json">{JSON.stringify(schema).replace(/</g, "\\u003c")}</script>
      </Helmet>

      <section className="st-hero" aria-labelledby="tehuacan-title">
        <div className="st-pattern" aria-hidden="true"><span className="st-orbit" /></div>
        <div className="st-container st-split st-hero-inner">
          <div>
            <p className="st-label st-location"><MapPin aria-hidden="true" />{t.eyebrow}</p>
            <span className="st-accent" aria-hidden="true" />
            <h1 id="tehuacan-title" className="st-display st-hero-title">{t.heroLead}<span>Tehuacán</span></h1>
            <p className="st-hero-subtitle">{t.heroAudience}</p>
            <p className="st-hero-copy">{t.heroText}</p>
            <div className="st-actions">
              <Link to="/asesoria" className="st-button st-primary">{t.book}<ArrowUpRight aria-hidden="true" /></Link>
              <Link to="/servicios" className="st-button st-secondary">{t.viewServices}</Link>
            </div>
            <p className="st-language-note">{t.languages}</p>
          </div>
          <aside className="st-panel" aria-labelledby="tehuacan-needs-title">
            <div className="st-rule" aria-hidden="true" />
            <h2 id="tehuacan-needs-title" className="st-display st-panel-title">{t.needsTitle}</h2>
            <ul className="st-needs">
              {t.needs.map((item, index) => (
                <li key={index}><span className="st-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span><span className="st-needs-text">{item}</span></li>
              ))}
            </ul>
            <p className="st-panel-note">{t.needsNote}</p>
          </aside>
        </div>
      </section>

      <div className="st-container">
        <nav className="st-breadcrumb" aria-label={t.breadcrumb}>
          <ol><li><Link to="/">{t.home}</Link></li><li aria-hidden="true">/</li><li aria-current="page">Tehuacán</li></ol>
        </nav>
      </div>

      <section className="st-section" aria-labelledby="tehuacan-intro-title">
        <div className="st-container st-split st-intro">
          <div>
            <p className="st-label st-blue">{t.introLabel}</p>
            <h2 id="tehuacan-intro-title" className="st-display st-title">{t.introTitle}</h2>
            <span className="st-accent" aria-hidden="true" />
          </div>
          <div className="st-copy">{t.introParagraphs.map((paragraph, index) => <p key={index}>{paragraph}</p>)}</div>
        </div>
      </section>

      <section id="servicios-tehuacan" className="st-section st-services" aria-labelledby="tehuacan-services-title">
        <div className="st-container">
          <div className="st-heading">
            <p className="st-label st-blue">{t.servicesLabel}</p>
            <h2 id="tehuacan-services-title" className="st-display st-title">{t.servicesTitle}</h2>
            <p className="st-copy">{t.servicesText}</p>
          </div>
          <div className="st-grid st-service-grid">
            {t.services.map((service, index) => {
              const Icon = SERVICE_ICONS[index];
              return (
                <article className="st-service" key={index}>
                  <div className="st-service-top" aria-hidden="true"><Icon /><span>{String(index + 1).padStart(2, "0")}</span></div>
                  <h3 className="st-display st-card-title">{service.title}</h3>
                  <p>{service.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="st-section" aria-labelledby="tehuacan-audience-title">
        <div className="st-container">
          <div className="st-split st-audience">
            <div>
              <p className="st-label">{t.audienceLabel}</p>
              <h2 id="tehuacan-audience-title" className="st-display st-title">{t.audienceTitle}</h2>
              <p className="st-audience-copy">{t.audienceText}</p>
            </div>
            <ul className="st-clients">{t.clients.map((item, index) => <li key={index}><CheckCircle2 aria-hidden="true" /><span>{item}</span></li>)}</ul>
          </div>
        </div>
      </section>

      <section className="st-section st-process" aria-labelledby="tehuacan-process-title">
        <div className="st-container">
          <p className="st-label st-blue">{t.processLabel}</p>
          <h2 id="tehuacan-process-title" className="st-display st-title">{t.processTitle}</h2>
          <ol className="st-grid st-step-grid">
            {t.steps.map((step, index) => (
              <li className="st-step" key={index}>
                <span className="st-display st-step-number" aria-hidden="true">{String(index + 1).padStart(2, "0")}</span>
                <h3 className="st-display st-card-title">{step.title}</h3><p>{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="st-section st-faq-section" aria-labelledby="tehuacan-faq-title">
        <div className="st-container st-split st-faq-layout">
          <div>
            <p className="st-label st-blue">{t.faqLabel}</p>
            <h2 id="tehuacan-faq-title" className="st-display st-title">{t.faqTitle}</h2>
            <span className="st-accent" aria-hidden="true" />
          </div>
          <div className="st-faq">
            {t.faqs.map((faq, index) => (
              <details key={index}>
                <summary><span>{faq.q}</span><span className="st-faq-plus" aria-hidden="true">+</span></summary>
                <p>{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="st-section st-cta" aria-labelledby="tehuacan-cta-title">
        <div className="st-container st-cta-inner">
          <div className="st-cta-copy">
            <p className="st-label">{t.ctaLabel}</p>
            <h2 id="tehuacan-cta-title" className="st-display st-title">{t.ctaTitle}</h2>
            <p>{t.ctaText}</p>
          </div>
          <Link to="/asesoria" className="st-button st-primary">{t.book}<ArrowUpRight aria-hidden="true" /></Link>
        </div>
      </section>
    </div>
  );
}